/* Strum the target chord on the guitar (chroma detection), or pick it on screen without a mic. */
import { useEffect, useRef, useState } from 'react';
import { audio } from '../../../core/audio';
import { matchChordFrame, onMic } from '../../../core/mic';
import { chordSymbol, pretty } from '../../../core/theory';
import { Btn } from '../../../ui/Button';
import { ChordDiagram } from '../../../ui/ChordDiagram';
import { ProgressBar } from '../../../ui/widgets';
import type { ChoiceQuestion, ChordQuestion, ChordTarget, QuestionApi } from '../types';
import { ChoiceArea } from './ChoiceArea';

const chordName = (c: ChordTarget, flat?: boolean) => pretty(c.sym || chordSymbol(c.root, c.q, flat));

export function ChordArea({ q, api }: { q: ChordQuestion; api: QuestionApi }) {
  if (api.input !== 'mic') {
    const opts = q.screenOptions || q.vocab.slice(0, 8);
    const choiceQ: ChoiceQuestion = {
      ...q,
      type: 'choice',
      options: opts.map((c) => ({ id: `${c.root}|${c.q}`, label: chordName(c, q.flat) })),
      answer: `${q.target.root}|${q.target.q}`,
      maxMiss: q.maxMiss || 2,
      preview: (id) => {
        const [r, qq] = String(id).split('|');
        return audio.playChord({ root: Number(r), q: qq as ChordTarget['q'] });
      },
    };
    return (
      <>
        <p className="ex-hint">Mikrofon kapalı: duyduğun akoru seç.</p>
        <ChoiceArea q={choiceQ} api={api} />
      </>
    );
  }
  return <ChordLive q={q} api={api} />;
}

function ChordLive({ q, api }: { q: ChordQuestion; api: QuestionApi }) {
  const [heard, setHeard] = useState('—');
  const [conf, setConf] = useState(0);
  const [selfOpen, setSelfOpen] = useState(false);
  const stable = useRef(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: one listener per mounted question
  useEffect(() => {
    let wrongName: string | null = null;
    let wrongCount = 0;
    const reported = new Set<string>();
    return onMic('chord', (c) => {
      if (api.isDone()) return;
      if (!c.active) {
        stable.current = Math.max(0, stable.current - 1);
        setConf(stable.current / 4);
        return;
      }
      const top = matchChordFrame(c, q.vocab)[0];
      if (!top) return;
      const nm = chordSymbol(top.root, top.q, q.flat);
      setHeard(pretty(nm));
      const isTarget = top.root === q.target.root && top.q === q.target.q;
      if (isTarget && top.score > 0.55) {
        stable.current++;
        setConf(stable.current / 4);
        if (stable.current >= 4) api.done(true);
        return;
      }
      stable.current = Math.max(0, stable.current - 1);
      setConf(stable.current / 4);
      if (top.score > 0.6) {
        if (wrongName === nm) wrongCount++;
        else {
          wrongName = nm;
          wrongCount = 1;
        }
        if (wrongCount === 7 && !reported.has(nm)) {
          reported.add(nm);
          api.feedback(`Çaldığın akor **${pretty(nm)}** gibi duyuluyor. Bas notasını dinleyip tekrar dene.`, 'bad');
          api.miss({ played: nm });
        }
      }
    });
  }, [api]);

  const target = q.target.sym || chordSymbol(q.target.root, q.target.q);
  return (
    <>
      <div className="chord-live">
        <span className="cl-label">Duyulan akor</span>
        <span className="cl-name">{heard}</span>
        <ProgressBar frac={conf} />
      </div>
      <p className="ex-hint">Akoru bulunca birkaç kez çal. Doğru akor yarım saniye kadar duyulunca onaylanır.</p>
      <button type="button" className="link" onClick={() => setSelfOpen(true)}>
        Algılama takıldı mı? Cevaba bakıp kendin işaretle
      </button>
      <div className="selfcheck subtle">
        {selfOpen ? (
          <>
            <p>
              Doğru akor: <strong>{chordName(q.target, q.flat)}</strong>. Sen de bunu mu çaldın?
            </p>
            <ChordDiagram chord={target} />
            <div className="sc-actions">
              <Btn kind="primary" onClick={() => api.done(true, { self: true })}>
                Evet, bunu çaldım
              </Btn>
              <Btn kind="ghost" onClick={() => api.done(false, { self: true })}>
                Hayır
              </Btn>
            </div>
          </>
        ) : null}
      </div>
    </>
  );
}
